import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';

import {
  AccountManager,
  btag,
  CasinoFavoritesProvider,
  CasinoGamesProvider,
  CategoryGamesProvider,
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  pushToDataLayer,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useCasinoNewState,
  useSettings,
} from '@atlas/core';
import {
  CasinoScrollableContainer,
  EmptyStateView,
  Loader,
  ViewportHeightContainer,
} from '@atlas/ui';

import CasinoStaticBlock from 'common/StaticBlock/CasinoStaticBlock';
import { CasinoBanner } from 'components/Casino/CasinoBanner';
import { CasinoFavoritesGames } from 'components/Casino/CasinoFavoritesGames';
import { useCasinoFilterState } from 'components/Casino/CasinoFilterContext';
import { CasinoCategoryGames } from 'components/Casino/CasinoGamesList';
import CasinoLobby from 'components/Casino/CasinoLobby/CasinoLobby';
import CasinoHomeNavigation from 'components/Casino/components/CasinoHomeNavigation';
import CasinoInlineFilter from 'components/Casino/components/CasinoInlineFilter';
import CasinoLastWinners from 'components/Casino/components/CasinoLastWinners/CasinoLastWinners';
import CasinoNavigation from 'components/Casino/components/CasinoNavigation';
import HomePageGames from 'components/Casino/components/HomePageGames';
import ProvidersFilter from 'components/Casino/components/ProvidersFilter';
import { ProvidersFilterBlock } from 'components/Casino/components/ProvidersFilterBlock';
import { GROUPS } from 'components/Casino/constants';
import SearchResults from 'components/Casino/SearchResults';
import { casinoFilterTypeMap, getFilter } from 'components/Casino/useCasinoFilter';
import SeoPageText from 'components/SeoPage/SeoPageText';
import FooterSticky from 'src/components/Footer/FooterSticky';
import QuickDepositPopup from 'src/components/QuickDepositPopup/QuickDepositPopup';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { getSearchParam } from 'src/helpers/getSearchParam';

const CATEGORY_GAMES_COUNT = 20;
const BALANCE_MULTIPLIER = 100;
const MAX_DELAY_COUNT = 3;
export const QUICK_DEPOSIT_DELAY = 'quick_deposit_delay';

const casinoGroupSource = {
  [GROUPS.CASINO]: 'casino-home-sidebar',
  [GROUPS.LIVE]: 'live-casino-home-sidebar',
};

const quickDepositGroups = {
  [GROUPS.CASINO]: 'casino_page',
  [GROUPS.LIVE]: 'live_casino_page',
};

const MainCasinoPage = () => {
  const { push: historyPush } = useHistory();
  const { state } = useLocation();
  const { accountBlocked, redirectAfterPopup, selfRegulationPopupShow, showRealityCheckPopup } =
    state || {};
  const { account, authenticated, bankAccounts, wallet } = useApplicationState();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const showSpentLimitPopupIfNeededRef = useRef(showSpentLimitPopupIfNeeded);
  showSpentLimitPopupIfNeededRef.current = showSpentLimitPopupIfNeeded;
  const showSelfRegulationPopupIfNeededRef = useRef(showSelfRegulationPopupIfNeeded);
  showSelfRegulationPopupIfNeededRef.current = showSelfRegulationPopupIfNeeded;
  const { BONUS = 0, CASH = 0, FROZEN = 0 } = wallet.detailedBalance || {};
  const balance = BONUS + CASH + FROZEN;
  const {
    categories: { error: categoriesError, loaded: categoriesLoaded },
    group,
    providers: { error: providersError },
  } = useCasinoNewState();
  const { filter, updateFilter } = useCasinoFilterState();
  const { lang } = useSettings();
  const searchCategory = getSearchParam(window.location.search, 'category');
  const searchProviderNames = getSearchParam(window.location.search, 'providerNames');
  const searchQuery = getFilter(casinoFilterTypeMap.name) || '';
  const scrollParentEl = useRef(null);
  const [activeItem, setActiveItem] = useState();
  const [showFavorites, setShowFavorites] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(!!searchQuery);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(true);
  const [showQuickDepositPopup, setShowQuickDepositPopup] = useState(false);
  const resetAfterHoursTimeoutId = useRef(null);
  const showQuickDepositTimeoutId = useRef(null);
  const homePageFunctionality = SiteConfigManager.getConfig(
    'config.config.casino.desktop_home_page',
  );
  const quickDepositEnabled = SiteConfigManager.getConfig(
    'config.config.general.quickDepositPopup.enabled',
  );
  const quickDepositMinBalance = SiteConfigManager.getConfig(
    'config.config.general.quickDepositPopup.minBalance',
  );
  const resetAfterHours = SiteConfigManager.getConfig(
    'config.config.general.quickDepositPopup.reset_after_hours',
  );

  const quickDepositPages = useMemo(
    () => SiteConfigManager.getConfig('config.config.general.quickDepositPopup.pages'),
    [],
  );

  const source = casinoGroupSource[group];

  const isHomePage = activeItem !== 'all_games' && filter.categories.length === 0;

  const isCasinoLobbyConstructorEnabled = SiteConfigManager.getConfig(
    'config.config.casino.isCasinoLobbyConstructorEnabled',
    false,
  );

  const casinoLobbyConfig = SiteConfigManager.getConfig('casino_lobby_config', null);

  const shouldRenderLobby = useMemo(() => {
    const structure = casinoLobbyConfig?.lobbyStructure;
    return (
      isCasinoLobbyConstructorEnabled &&
      Array.isArray(structure) &&
      structure.length > 0 &&
      structure.every(block => block?.type) &&
      !isSearchActive &&
      !showFavorites &&
      homePageFunctionality &&
      isHomePage
    );
  }, [
    isCasinoLobbyConstructorEnabled,
    casinoLobbyConfig,
    isSearchActive,
    showFavorites,
    homePageFunctionality,
    isHomePage,
  ]);

  const closeQuickDepositPopup = useCallback(() => {
    setShowQuickDepositPopup(false);
  }, [setShowQuickDepositPopup]);

  const closeQuickDepositPopupWithSetDelay = useCallback(() => {
    setShowQuickDepositPopup(false);

    const delayInfo = StorageManager.getItem(QUICK_DEPOSIT_DELAY);
    const { delay = 0 } = delayInfo ? JSON.parse(delayInfo) : {};
    const currentDelay = delay === MAX_DELAY_COUNT ? MAX_DELAY_COUNT : delay + 1;
    const delayAfterClose = SiteConfigManager.getConfig(
      `config.config.general.quickDepositPopup.delay_after_close_${currentDelay}`,
    );

    const currentDelayInfo = {
      delay: currentDelay,
      delayStartTime: Date.now(),
    };

    StorageManager.setItem(QUICK_DEPOSIT_DELAY, JSON.stringify(currentDelayInfo));

    showQuickDepositTimeoutId.current = setTimeout(() => {
      setShowQuickDepositPopup(true);
    }, delayAfterClose * 1000);

    if (resetAfterHoursTimeoutId.current) {
      clearTimeout(resetAfterHoursTimeoutId.current);
    }

    if (resetAfterHours) {
      const resetDelay = resetAfterHours * 3600 * 1000;

      resetAfterHoursTimeoutId.current = setTimeout(() => {
        StorageManager.removeItem(QUICK_DEPOSIT_DELAY);
      }, resetDelay);
    }
  }, [resetAfterHours]);

  const showQuickDepositPopupWithDelay = useCallback(delayInfo => {
    const { delay, delayStartTime } = JSON.parse(delayInfo);

    const delayAfterClose = SiteConfigManager.getConfig(
      `config.config.general.quickDepositPopup.delay_after_close_${delay}`,
    );
    const timeToShowPopup = delayStartTime + delayAfterClose * 1000;
    const currentDelay = Math.max(timeToShowPopup - Date.now(), 0);

    if (currentDelay > 0) {
      showQuickDepositTimeoutId.current = setTimeout(() => {
        setShowQuickDepositPopup(true);
      }, currentDelay);
    } else {
      setShowQuickDepositPopup(true);
    }
  }, []);

  const resetQuickDepositDelay = useCallback(() => {
    const delayInfo = StorageManager.getItem(QUICK_DEPOSIT_DELAY);

    if (!delayInfo || !resetAfterHours) return;

    const { delayStartTime } = JSON.parse(delayInfo);
    const timeToResetDelay = delayStartTime + resetAfterHours * 3600 * 1000;
    const resetDelay = timeToResetDelay - Date.now();

    if (resetDelay > 0) {
      resetAfterHoursTimeoutId.current = setTimeout(() => {
        StorageManager.removeItem(QUICK_DEPOSIT_DELAY);
      }, resetDelay);
    } else {
      StorageManager.removeItem(QUICK_DEPOSIT_DELAY);
    }
  }, [resetAfterHours]);

  useEffect(() => {
    if (accountBlocked) {
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
    }

    if (selfRegulationPopupShow || showRealityCheckPopup) {
      (async () => {
        const spentLimitSuccess = await showSpentLimitPopupIfNeededRef.current();

        if (!spentLimitSuccess) return;

        const selfRegulationSuccess = await showSelfRegulationPopupIfNeededRef.current();

        if (redirectAfterPopup && selfRegulationSuccess) {
          historyPush(redirectAfterPopup);
        }
      })();
    }
  }, [
    accountBlocked,
    historyPush,
    redirectAfterPopup,
    selfRegulationPopupShow,
    showRealityCheckPopup,
  ]);

  useEffect(() => {
    const { clientId } = AccountManager.account;

    pushToDataLayer(
      'open-casino',
      {
        client: { id: clientId },
        language: lang,
      },
      btag,
    );
  }, [lang]);

  useEffect(() => {
    if (
      !authenticated ||
      !quickDepositEnabled ||
      !quickDepositPages.includes(quickDepositGroups[group]) ||
      (needsVerification(account, bankAccounts) &&
        isUserActionNeedPhotoVerification(account, PhotoVerificationAction.DEPOSIT))
    ) {
      setShowQuickDepositPopup(false);
      return;
    }

    resetQuickDepositDelay();

    const delayInfo = StorageManager.getItem(QUICK_DEPOSIT_DELAY);

    if (balance < quickDepositMinBalance * BALANCE_MULTIPLIER) {
      if (delayInfo) {
        showQuickDepositPopupWithDelay(delayInfo);
      } else {
        setShowQuickDepositPopup(true);
      }
    } else {
      if (showQuickDepositTimeoutId.current) {
        clearTimeout(showQuickDepositTimeoutId.current);
      }

      if (delayInfo) {
        StorageManager.removeItem(QUICK_DEPOSIT_DELAY);
      }
    }
  }, [
    account,
    authenticated,
    balance,
    bankAccounts,
    group,
    quickDepositEnabled,
    quickDepositMinBalance,
    quickDepositPages,
    resetQuickDepositDelay,
    showQuickDepositPopupWithDelay,
  ]);

  useEffect(() => {
    const categoryFilter = filter.categories[0];

    if (
      (!searchCategory && !!categoryFilter !== !!searchCategory) ||
      (searchCategory && categoryFilter !== searchCategory)
    ) {
      const newCategoriesFilter = searchCategory ? [searchCategory] : [];

      updateFilter({ categories: newCategoriesFilter });
    }
  }, [filter.categories, searchCategory, updateFilter]);

  // function to reset showProviders variable to be true
  const resetPageToShowProviders = useCallback(() => {
    setIsSearchActive(false);
    setShowFavorites(false);
    setActiveItem('all_games');
    updateFilter({ categories: [] });
  }, [updateFilter]);

  useEffect(() => {
    // filter by provider on mount when there is providerNames search param
    if (searchProviderNames?.split(',')?.length > 0) {
      resetPageToShowProviders();
    }
  }, [resetPageToShowProviders, searchProviderNames]);

  useEffect(() => {
    return () => {
      clearTimeout(showQuickDepositTimeoutId.current);
      clearTimeout(resetAfterHoursTimeoutId.current);
    };
  }, []);

  const renderGamesList = useMemo(() => {
    if (isSearchActive) {
      return (
        <SearchResults group={group} searchQuery={searchQuery} setGamesLoading={setGamesLoading} />
      );
    }

    if (showFavorites) {
      return (
        <CasinoFavoritesProvider>
          <CasinoFavoritesGames />
        </CasinoFavoritesProvider>
      );
    }

    if (homePageFunctionality && isHomePage) {
      return (
        <>
          <CategoryGamesProvider gamesCount={CATEGORY_GAMES_COUNT} group={group}>
            <HomePageGames isFullWidth={isFullWidth} setActiveItem={setActiveItem} />
            <ProvidersFilterBlock
              onProviderClick={() => {
                resetPageToShowProviders();
                if (scrollParentEl?.current) scrollParentEl.current.scrollIntoView();
              }}
              source={source}
            />
          </CategoryGamesProvider>
        </>
      );
    }

    return (
      <CasinoGamesProvider
        categories={filter.categories}
        group={group}
        name=""
        providers={filter.providerNames}
      >
        <CategoryGamesProvider gamesCount={CATEGORY_GAMES_COUNT} group={group}>
          <CasinoCategoryGames setActiveItem={setActiveItem} />
        </CategoryGamesProvider>
      </CasinoGamesProvider>
    );
  }, [
    filter.categories,
    filter.providerNames,
    group,
    homePageFunctionality,
    isFullWidth,
    isHomePage,
    isSearchActive,
    resetPageToShowProviders,
    searchQuery,
    showFavorites,
    source,
  ]);

  if (categoriesError || providersError) {
    return (
      <EmptyStateView
        className="viewport-height"
        description="page_loading_error"
        title="page_loading_error_title"
      />
    );
  }

  const showProviders =
    !isSearchActive &&
    !showFavorites &&
    (!homePageFunctionality || (homePageFunctionality && !isHomePage));

  return (
    <>
      <div className="casino">
        <ViewportHeightContainer className="page__main" id="main">
          <div className="container-fluid">
            <div className="page__wrapper">
              <CasinoScrollableContainer
                className="event-list casino__content viewport-height"
                id="content"
                parentRef={scrollParentEl}
              >
                <div className="casino__sliders">
                  <CasinoLastWinners />
                  <CasinoBanner />
                </div>

                <div className="casino-wrap">
                  {homePageFunctionality ? (
                    <CasinoHomeNavigation
                      activeItem={activeItem}
                      isSearchActive={isSearchActive}
                      loading={gamesLoading}
                      setActiveItem={setActiveItem}
                      setIsSearchActive={setIsSearchActive}
                      setShowFavorites={setShowFavorites}
                      showFavorites={showFavorites}
                    />
                  ) : (
                    <>
                      {categoriesLoaded ? (
                        <CasinoNavigation
                          classNames={!showProviders && !isSearchActive && 'with-margin'}
                          isSearchActive={isSearchActive}
                          loading={gamesLoading}
                          setIsSearchActive={setIsSearchActive}
                          setShowFavorites={setShowFavorites}
                          showFavorites={showFavorites}
                        />
                      ) : (
                        <Loader />
                      )}
                    </>
                  )}
                  {showProviders && <>{categoriesLoaded ? <ProvidersFilter /> : <Loader />}</>}
                </div>

                <div className="casino__layout__wrapper">
                  {homePageFunctionality && isHomePage && !isSearchActive && (
                    <div className="casino__sidebar">
                      <CasinoStaticBlock name={source} setIsFullWidth={setIsFullWidth} />
                    </div>
                  )}
                  <div className="casino__layout viewport-height__position">
                    {showProviders && <CasinoInlineFilter />}

                    {shouldRenderLobby ? (
                      <>
                        <CasinoLobby
                          onProviderClick={() => {
                            resetPageToShowProviders();
                            if (scrollParentEl?.current) scrollParentEl.current.scrollIntoView();
                          }}
                          setActiveItem={setActiveItem}
                          source={source}
                        />
                      </>
                    ) : (
                      <>{renderGamesList}</>
                    )}
                  </div>
                </div>
              </CasinoScrollableContainer>
            </div>
          </div>
        </ViewportHeightContainer>

        <SeoPageText />

        {showQuickDepositPopup && (
          <QuickDepositPopup
            onClose={closeQuickDepositPopup}
            onCloseClick={closeQuickDepositPopupWithSetDelay}
          />
        )}
      </div>

      {createPortal(<FooterSticky />, document.getElementById('root'))}
    </>
  );
};

MainCasinoPage.whyDidYouRender = true;

export default MainCasinoPage;
